import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { TriangleAlert } from "lucide-react";
import { ContactFooter } from "@/components/ContactFooter";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (password !== confirm) {
      toast.error("Le due password non coincidono");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password aggiornata. Ora puoi accedere con la nuova password.");
      navigate("/login");
    } catch (err) {
      toast.error(errMsg(err, "Link non valido o scaduto. Richiedi un nuovo link."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-5 pt-[14vh] fade-up">
        <div className="flex items-center gap-2 mb-10">
          <span className="inline-block w-3 h-3 rounded-full bg-[color:var(--volt)] border border-black/10" aria-hidden="true" />
          <span className="font-extrabold tracking-tight uppercase">Propedeutico</span>
        </div>

        {!token ? (
          <div data-testid="reset-password-missing-token">
            <div className="w-12 h-12 rounded-full bg-[color:var(--bg-subtle)] border border-[color:var(--hairline)] flex items-center justify-center mb-6">
              <TriangleAlert className="w-5 h-5 text-[color:var(--danger)]" aria-hidden="true" />
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight leading-tight">Link non valido</h1>
            <p className="mt-3 text-[color:var(--text-2)] leading-6">
              Questo link di reimpostazione è incompleto o non valido. Richiedi un nuovo link dalla pagina di recupero password.
            </p>
            <Link
              to="/password-dimenticata"
              data-testid="reset-password-request-new-link"
              className="press mt-8 inline-flex items-center justify-center rounded-full bg-[color:var(--ink)] text-white font-extrabold px-6 py-3.5 min-h-[44px] hover:bg-black/85 transition-colors duration-200"
            >
              Richiedi un nuovo link
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-extrabold tracking-tight leading-tight">Scegli una nuova password</h1>
            <p className="mt-3 text-[color:var(--text-2)] leading-6">
              Imposta la nuova password del tuo account. Dopo il salvataggio verrai riportato alla pagina di accesso.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label htmlFor="new-password" className="block text-sm font-semibold mb-1.5">Nuova password</label>
                <input
                  id="new-password"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-new-input"
                  className="w-full rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3.5 text-sm focus:border-[color:var(--ink)] outline-none transition-colors duration-200"
                  placeholder="Almeno 6 caratteri"
                />
              </div>
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-semibold mb-1.5">Conferma password</label>
                <input
                  id="confirm-password"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-password-confirm-input"
                  className="w-full rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3.5 text-sm focus:border-[color:var(--ink)] outline-none transition-colors duration-200"
                  placeholder="Ripeti la password"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-password-submit-button"
                className="press w-full rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-3.5 min-h-[44px] hover:bg-black/85 disabled:opacity-60 transition-colors duration-200"
              >
                {busy ? "Salvataggio…" : "Salva nuova password"}
              </button>
            </form>

            <p className="mt-6 text-sm text-[color:var(--text-2)]">
              Il link non funziona?{" "}
              <Link to="/password-dimenticata" className="font-bold text-[color:var(--ink)] underline underline-offset-2" data-testid="reset-password-goto-forgot-link">
                Richiedine uno nuovo
              </Link>
            </p>
          </>
        )}

        <ContactFooter />
        <p className="mt-6 pb-10 text-xs text-[color:var(--text-2)]">App di supporto alla preparazione. Non è un prodotto ufficiale FIGC.</p>
      </div>
    </div>
  );
}
